import { type AlignProposalQueryParams, getAlignProposal } from '@repo/api-adapters/services/align-proposal.api';
import { type ServicesBasePath } from '@repo/api-adapters/services/services.utils';
import { type AlignProposalPayload } from '@repo/types/align-proposal';
import { type QueryClient, useQueries } from '@tanstack/react-query';

import { API } from './utils.api';

export interface AlignProposalRequest {
    timeWindowId: string;
    payload: AlignProposalPayload;
    queryParams: AlignProposalQueryParams;
}

interface UseAlignProposalsParams {
    servicesBasePath: ServicesBasePath;
    requests: AlignProposalRequest[];
}

const getServiceLinesKey = (payload: AlignProposalPayload) =>
    payload.serviceLines.map((serviceLine) => [
        serviceLine.serviceNo,
        serviceLine.serviceCode,
        serviceLine.serviceProviderId,
        serviceLine.capacityNeeded,
        serviceLine.capacityUnit,
        serviceLine.timeWindowId,
        serviceLine.manualChangeType,
        serviceLine.fromDate,
        serviceLine.transportTimewindows.map((transportTimewindow) => [
            transportTimewindow.transportTimewindowId,
            transportTimewindow.start,
            transportTimewindow.end,
            transportTimewindow.serviceIdentifier,
        ]),
    ]);

const getAlignProposalQueryKey = ({
    servicesBasePath,
    payload,
    queryParams,
}: {
    servicesBasePath: ServicesBasePath;
    payload: AlignProposalPayload | undefined;
    queryParams: AlignProposalQueryParams | undefined;
}) => {
    if (!payload) {
        return [API.alignProposal, servicesBasePath];
    }

    return [
        API.alignProposal,
        servicesBasePath,
        queryParams?.country,
        queryParams?.buCode,
        queryParams?.zipCode,
        payload.locality,
        getServiceLinesKey(payload),
    ];
};

export const fetchAlignProposal = (
    queryClient: QueryClient,
    servicesBasePath: ServicesBasePath,
    payload: AlignProposalPayload,
    queryParams: AlignProposalQueryParams
) =>
    queryClient.fetchQuery({
        queryKey: getAlignProposalQueryKey({ servicesBasePath, payload, queryParams }),
        queryFn: () => getAlignProposal(servicesBasePath, payload, queryParams),
    });

export const useAlignProposals = ({ servicesBasePath, requests }: UseAlignProposalsParams) => {
    return useQueries({
        queries: requests.map(({ payload, queryParams }) => ({
            queryKey: getAlignProposalQueryKey({ servicesBasePath, payload, queryParams }),
            queryFn: async () => getAlignProposal(servicesBasePath, payload, queryParams),
        })),
    });
};
